import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { StatusBadge, SignalBars, BatteryLevel } from "../components/tank_widgets";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import { useFacilityStore } from "../../../../../../app/z-store/store";
import { getWaterSensorTanksURL } from "../../../../../../utils/urls";
import { formatLitres, formatMetres, formatPercent, tankStatus, timeAgo } from "../utils/format";
import { viewTankPath } from "../utils/routes";

const REFRESH_MS = 60000;

function TanksTab() {
  const navigate = useNavigate();
  const facilityId = useFacilityStore((state) => state.facilityId);
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const getTanks = async () => {
    if (!facilityId) return;
    try {
      const response = await makeRequest2(getWaterSensorTanksURL(facilityId), "GET");
      if (response.success) setTanks(response.data.data || []);
      else toastify(response.error, "error");
    } catch (err) {
      toastify(err.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setTanks([]);
    getTanks();
    const refresh = setInterval(getTanks, REFRESH_MS);
    return () => clearInterval(refresh);
  }, [facilityId]);

  const filtered = tanks.filter((t) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return [t.name, t.location, t.gatewayId, String(t.slot)].some((v) => (v || "").toLowerCase().includes(q));
  });

  const nameTemplate = (tank) => (
    <div>
      <div className="fw-semibold">{tank.name}</div>
      <div className="text-muted small">{tank.location || "—"}</div>
    </div>
  );
  const levelTemplate = (tank) => (
    <div>
      <div className="fw-semibold">{formatPercent(tank.last?.pct)}</div>
      <div className="text-muted small">{formatMetres(tank.last?.levelM)}</div>
    </div>
  );
  const actionTemplate = (tank) => (
    <button className="btn btn-sm btn-outline-primary" onClick={() => navigate(viewTankPath(tank._id))}>
      <i className="ti ti-eye me-1"></i> View
    </button>
  );

  return (
    <div className="card">
      <div className="card-header d-flex flex-wrap gap-2 justify-content-between align-items-center">
        <h5 className="mb-0">Tanks</h5>
        <input
          type="search"
          className="form-control form-control-sm table-search"
          placeholder="Search name, location, slot…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>
      <div className="card-body">
        <DataTable
          value={filtered}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="No tanks in this facility yet. Assign its gateway on the Gateways tab; tanks appear when the first reading arrives."
          sortField="slot"
          sortOrder={1}
          stripedRows
          responsiveLayout="scroll"
        >
          <Column header="Tank" body={nameTemplate} sortable sortField="name" />
          <Column field="slot" header="Slot" sortable />
          <Column field="gatewayId" header="Gateway" sortable />
          <Column header="Level" body={levelTemplate} sortable sortField="last.pct" />
          <Column header="Volume" body={(t) => formatLitres(t.last?.volumeL)} />
          <Column header="Battery" body={(t) => <BatteryLevel pct={t.last?.battery} low={t.last?.battery != null && t.last.battery <= t.batteryLowPct} />} />
          <Column header="Signal" body={(t) => <SignalBars rssi={t.last?.rssi} />} />
          <Column header="Status" body={(t) => <StatusBadge status={tankStatus(t)} />} />
          <Column header="Last reading" body={(t) => timeAgo(t.last?.ts)} />
          <Column header="Actions" body={actionTemplate} />
        </DataTable>
      </div>
    </div>
  );
}

export default TanksTab;
